import React from 'react';
import { apiClient } from '../../../lib/apiClient';
import { DMA_REGIONS } from '../../../data/dmaList';
import { transformFeature } from './usMapProjection';

// CTV-445: national DMA boundaries for the report's DMA choropleth. Fetched from
// the selfservice-api POST /geographic/dma-boundaries (ST_AsGeoJSON over
// census_dmas.boundary) — the DMA analogue of the ZIP heatmap's boundary fetch.
// We request every known DMA code so the map draws the full national base (all
// markets gray, delivered ones colored), then cache module-wide so toggling in
// and out of the DMA view doesn't re-fetch.

interface DmaBoundary {
  dma_code: string;
  dma_name: string;
  boundary: GeoJSON.Geometry | null;
}

let cachedData: GeoJSON.FeatureCollection | null = null;
let inFlight: Promise<GeoJSON.FeatureCollection> | null = null;

async function fetchOnce(): Promise<GeoJSON.FeatureCollection> {
  if (cachedData) return cachedData;
  if (inFlight) return inFlight;
  const dmaCodes = DMA_REGIONS.map((d) => d.code);
  inFlight = apiClient
    .post<{ dmas: DmaBoundary[] }>('/geographic/dma-boundaries', { dmaCodes })
    .then((r) => {
      const features = (r.dmas ?? [])
        .filter((d) => d.boundary)
        .map((d) =>
          transformFeature({
            type: 'Feature',
            geometry: d.boundary as GeoJSON.Geometry,
            properties: { dma_code: d.dma_code, dma_name: d.dma_name },
          }),
        );
      cachedData = { type: 'FeatureCollection', features };
      return cachedData;
    });
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

export interface DmaBoundariesResult {
  data: GeoJSON.FeatureCollection | null;
}

export function useDmaBoundaries(): DmaBoundariesResult {
  const [data, setData] = React.useState<GeoJSON.FeatureCollection | null>(cachedData);

  React.useEffect(() => {
    if (cachedData) return;
    let cancelled = false;
    fetchOnce()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData({ type: 'FeatureCollection', features: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data };
}
